import { animate, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";

interface CountUpProps {
  value: number;
  decimals?: number;
  duration?: number;
}

/**
 * Counts up from 0 to `value` when scrolled into view. Fires once.
 *
 * The final value is rendered on first paint so the real number is always
 * present for SEO, no-JS, and reduced-motion users — the animation only
 * rewinds to 0 at the moment it starts.
 */
export function CountUp({ value, decimals = 0, duration = 1.6 }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!inView || reduceMotion || !node) return;
    const controls = animate(0, value, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => {
        node.textContent = latest.toFixed(decimals);
      },
    });
    return () => controls.stop();
  }, [inView, reduceMotion, value, decimals, duration]);

  return <span ref={ref}>{value.toFixed(decimals)}</span>;
}
